import React from "react";
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import useProperties from "hooks/useProperties";
import { capitalizeWord } from "utils/commonUtils";
import { ReactComponent as MapIcon } from "assets/icons/location_big.svg";
import { ReactComponent as InfoIcon } from "assets/icons/info.svg";
import { Tooltip as ReactTooltip } from "react-tooltip";

const tickFormatter = (value) => {
  const maxLength = 6;
  if (value.length <= maxLength) return value;

  return `${value.slice(0, maxLength)}...`;
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip bg-white p-2 border rounded-lg shadow-sm ">
        <h4>{payload[0].payload.name}</h4>
        <p className="">
          Prezzo per Mq:{" "}
          <span className="text-primary font-bold">
            {Math.round(payload[0].value)}€
          </span>
        </p>
      </div>
    );
  }

  return null;
};

const AvgPriceSqMeterChart = ({
  data = null,
  tooltipId = "price_sq_meter_tooltip",
  tooltipContent = "Qui sono mostrate le medie dei prezzi al metro quadro delle proprietà in vendita nelle zone selezionate. Questi dati vengono recuperati da oltre 100 piattaforme immobiliari che monitoriamo.",
  emptyStateMessage = "Seleziona una zona per visualizzarne le statistiche di mercato",
}) => {
  const { dashboardMarketStatisticsProperties } = useProperties();

  // Use provided data or fallback to dashboard data
  const sourceData = data || dashboardMarketStatisticsProperties;

  // Transform the data for the chart
  const chartData = sourceData.map((item) => ({
    name: capitalizeWord(item.locationName), // Use locationName for X-axis
    priceSqm: parseFloat(item.averagePricePerSqm), // Use averagePricePerSqm as the value
  }));

  return (
    <div
      className="bg-white rounded-[16px] pr-4 pb-4 pt-[21.5px] h-[288px] border-[1px] border-[#E2E4E9]"
      style={{ paddingLeft: chartData?.length ? 0 : 16 }}
    >
      {chartData?.length > 0 ? (
        <>
          <div className="flex items-start justify-between pl-[22.52px]">
            <h2 className="text-[16px] font-semibold leading-5 text-[#1A202C] -tracking-[0.176px]">
              Prezzo Medio al Metro Quadro
            </h2>

            <div
              data-tooltip-id={tooltipId}
              data-tooltip-content={tooltipContent}
              className="cursor-pointer"
            >
              <InfoIcon />
            </div>
            {/* Info Tooltip */}
            <ReactTooltip
              id={tooltipId}
              place="top"
              className="!max-w-96 !z-10 !text-sm !text-gray-500 !bg-white border !border-gray-200 !rounded-lg !shadow-sm"
            />
          </div>
          <hr className="ml-[22.52px] mt-[21px] h-[0.68px] bg-[#E2E4E9]" />
          <div className="w-full h-[177.49px] mt-[33px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={chartData}
                margin={{ top: 0, right: 0, left: 5, bottom: -10 }}
              >
                <CartesianGrid
                  fill="white"
                  stroke="#E5E5EF"
                  horizontal={true}
                  vertical={false}
                  strokeWidth={0.49}
                />
                <XAxis
                  dataKey="name"
                  tickMargin={0}
                  stroke="#E5E5EF"
                  strokeWidth={1.5}
                  tick={{ fill: "#615E83", fontSize: 12 }}
                  tickLine={false}
                  tickFormatter={tickFormatter}
                  interval={0}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tickCount={4}
                  tick={{ fill: "#615E83", fontSize: 12 }}
                  domain={["dataMin", "auto"]}
                />
                <Tooltip
                  content={<CustomTooltip />}
                  cursor={{ fill: "transparent" }}
                />
                <Bar
                  dataKey="priceSqm"
                  barSize={20}
                  fill="#1DA1F2"
                  radius={[6.8, 6.8, 0, 0]}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </>
      ) : (
        <div className="flex h-full items-center justify-center flex-col">
          <MapIcon className="w-[63px]  text-[#1DA1F266]" />
          <p className="text-[16px] text-center mt-4 font-semibold leading-[20px] text-[#A5D9FA]">
            {emptyStateMessage}
          </p>
        </div>
      )}
    </div>
  );
};

export default AvgPriceSqMeterChart;
